import { Gem, ArrowRight } from 'lucide-react'
import type { Gem as GemType } from './types'

interface GemCardProps {
  gem: GemType
  onView?: () => void
  showStatus?: boolean
}

const statusConfig: Record<string, { bg: string; text: string }> = {
  new: {
    bg: 'bg-teal-100 dark:bg-teal-900/40',
    text: 'text-teal-700 dark:text-teal-300',
  },
  'in-progress': {
    bg: 'bg-amber-100 dark:bg-amber-900/40',
    text: 'text-amber-700 dark:text-amber-300',
  },
  reviewed: {
    bg: 'bg-emerald-100 dark:bg-emerald-900/40',
    text: 'text-emerald-700 dark:text-emerald-300',
  },
  archived: {
    bg: 'bg-stone-100 dark:bg-stone-800',
    text: 'text-stone-600 dark:text-stone-400',
  },
}

export function GemCard({ gem, onView, showStatus = true }: GemCardProps) {
  const status = statusConfig[gem.status] || statusConfig.new

  return (
    <div
      onClick={onView}
      className={`group bg-gradient-to-br from-amber-50 to-amber-100/50 dark:from-amber-950/30 dark:to-amber-900/20 border border-amber-200 dark:border-amber-800/50 rounded-lg p-4 ${
        onView ? 'cursor-pointer hover:border-amber-300 dark:hover:border-amber-700 hover:shadow-md' : ''
      } transition-all duration-200`}
    >
      <div className="flex items-start gap-4">
        {/* Gem Icon */}
        <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-amber-200/60 dark:bg-amber-800/40 flex items-center justify-center group-hover:bg-amber-300/60 dark:group-hover:bg-amber-700/50 transition-colors">
          <Gem className="w-4 h-4 text-amber-600 dark:text-amber-400" />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Subject */}
          <h5 className="text-sm font-medium text-stone-900 dark:text-stone-100 mb-1 group-hover:text-amber-700 dark:group-hover:text-amber-300 transition-colors">
            {gem.itemSubject}
          </h5>

          {/* Reason */}
          <p className="text-sm text-stone-600 dark:text-stone-400 leading-relaxed">
            {gem.reason}
          </p>

          {/* Tags and status */}
          <div className="flex flex-wrap items-center gap-2">
            {showStatus && (
              <span className={`text-xs font-medium px-2 py-0.5 rounded-full capitalize ${status.bg} ${status.text}`}>
                {gem.status.replace('-', ' ')}
              </span>
            )}
            {gem.tags.map((tag) => (
              <span
                key={tag}
                className="text-xs px-2 py-0.5 rounded-full bg-stone-200/80 dark:bg-stone-700/80 text-stone-600 dark:text-stone-400"
              >
                {tag}
              </span>
            ))}
          </div>
        </div>

        {/* Hover arrow */}
        {onView && (
          <div className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity self-center">
            <ArrowRight className="w-4 h-4 text-amber-500 dark:text-amber-400" />
          </div>
        )}
      </div>
    </div>
  )
}
